import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, MapPin, Zap, Calendar, Shield, Sparkles, Star, MessageSquare, Truck, Handshake, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { resolveImageUrl } from "@/lib/img";

const INTEL = {
  great:   { label: "Great Deal",   cls: "text-emerald-300", bg: "bg-emerald-500/15 border-emerald-400/40" },
  good:    { label: "Good Deal",    cls: "text-blue-300",    bg: "bg-blue-500/15 border-blue-400/40" },
  average: { label: "Average Deal", cls: "text-gray-300",    bg: "bg-gray-500/15 border-gray-400/40" },
};

export default function DealDetail() {
  const { dealId } = useParams();
  const { user } = useAuth();
  const [deal, setDeal] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [ratings, setRatings] = useState(null);
  const [similar, setSimilar] = useState([]);

  useEffect(() => {
    api.get(`/lease-deals/${dealId}`).then((r) => setDeal(r.data)).catch(() => setDeal(null));
    setSimilar([]);
    api.get(`/lease-deals/${dealId}/similar?limit=4`).then((r) => setSimilar(r.data || [])).catch(() => setSimilar([]));
    window.scrollTo(0, 0);
  }, [dealId]);

  useEffect(() => {
    if (deal?.dealer_source) {
      api.get(`/dealers/by-name/${encodeURIComponent(deal.dealer_source)}/ratings`)
        .then((r) => setRatings(r.data))
        .catch(() => setRatings(null));
    }
  }, [deal?.dealer_source]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", {
        type: "find_my_car",
        payload: {
          deal_id: dealId,
          deal_summary: deal ? `${deal.brand} ${deal.model} ${deal.trim} - $${deal.monthly_payment}/mo` : "",
          ...form,
        },
      });
      toast.success("Request received. A broker will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", notes: "" });
    } catch {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!deal) return <div className="max-w-[1200px] mx-auto px-5 py-20 text-gray-400">Loading…</div>;
  const usd = (n) => `$${Math.round(n).toLocaleString()}`;
  const intel = INTEL[deal.intelligence_score];

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12" data-testid="deal-detail-page">
      <Link to="/lease-deals" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-8" data-testid="back-to-deals">
        <ArrowLeft className="w-4 h-4" /> Back to deals
      </Link>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="rounded-2xl overflow-hidden border border-[#1A1A24] aspect-[16/10] relative bg-[#0A0A0E]">
            {deal.image_url ? (
              <img src={resolveImageUrl(deal.image_url)} alt={`${deal.brand} ${deal.model}`} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = "/placeholder-vehicle.svg"; }} />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs tracking-widest">NO IMAGE</div>
            )}
            {intel && (
              <span className={`absolute top-4 left-4 px-3 py-1.5 rounded-full border ${intel.bg} ${intel.cls} text-[11px] font-black tracking-widest flex items-center gap-1.5`} data-testid="deal-intel-badge">
                <Sparkles className="w-3 h-3" /> {intel.label.toUpperCase()}
              </span>
            )}
          </div>
          <div className="mt-6">
            <p className="label-mono">{deal.brand} · {deal.year}</p>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter mt-2">{deal.model}</h1>
            <p className="text-gray-400 mt-1">{deal.trim}</p>
          </div>

          {/* Intelligence card */}
          {intel && (
            <div className={`mt-7 border ${intel.bg} rounded-2xl p-6`} data-testid="deal-intel-card">
              <div className="flex items-start gap-3 mb-2">
                <Sparkles className={`w-5 h-5 ${intel.cls} mt-0.5`} />
                <div className="flex-1">
                  <div className="label-mono mb-1">555 Deal Intelligence</div>
                  <div className={`font-display text-xl ${intel.cls}`}>{deal.intelligence_headline || intel.label}</div>
                </div>
                {typeof deal.intelligence_leverage_pct === "number" && (
                  <div className="text-right">
                    <div className="text-[10px] text-gray-500 tracking-wider uppercase">vs Segment</div>
                    <div className={`font-display text-2xl ${deal.intelligence_leverage_pct >= 0 ? "text-emerald-300" : "text-yellow-300"}`}>
                      {deal.intelligence_leverage_pct >= 0 ? "−" : "+"}{Math.abs(deal.intelligence_leverage_pct).toFixed(1)}%
                    </div>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-300 mt-3 leading-relaxed">{deal.intelligence_reason}</p>
              <p className="text-[10px] text-gray-500 mt-3">Analysis grounded only in the verified numbers above. AI scoring — final terms subject to credit approval.</p>
            </div>
          )}

          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { l: "Monthly", v: usd(deal.monthly_payment) },
              { l: "Due at Signing", v: usd(deal.due_at_signing) },
              { l: "MSRP", v: usd(deal.msrp) },
              { l: "Term", v: `${deal.lease_term_months} mo` },
              { l: "Annual Miles", v: `${(deal.annual_mileage / 1000)}k` },
              { l: "Savings", v: usd(deal.savings || 0) },
            ].map((x) => (
              <div key={x.l} className="bg-[#0A0A0E] border border-[#1A1A24] rounded-xl p-4">
                <div className="label-mono mb-2">{x.l}</div>
                <div className="font-display text-xl">{x.v}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 space-y-3">
            <div className="flex items-center gap-3 text-sm text-gray-300"><MapPin className="w-4 h-4 text-silver" /> {deal.region}</div>
            {deal.expiration_date && <div className="flex items-center gap-3 text-sm text-gray-300"><Calendar className="w-4 h-4 text-silver" /> Offer expires {deal.expiration_date}</div>}
            {deal.is_ev && <div className="flex items-center gap-3 text-sm text-gray-300"><Zap className="w-4 h-4 text-emerald-400" /> EV tax credit eligible (lessor pass-through)</div>}
            {deal.incentives && <div className="flex items-center gap-3 text-sm text-gray-300"><Shield className="w-4 h-4 text-silver" /> {deal.incentives}</div>}
            {deal.source_url && (
              <a href={deal.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white" data-testid="deal-source-link">
                <ExternalLink className="w-4 h-4 text-silver" /> Source: {deal.source_name || "OEM Offer"}
              </a>
            )}
          </div>

          {/* Dealer Rating Block */}
          {deal.dealer_source && (
            <DealerRatingBlock dealerName={deal.dealer_source} ratings={ratings} setRatings={setRatings} dealId={deal.id} user={user} />
          )}

          <p className="text-[11px] text-gray-500 mt-8 max-w-2xl leading-relaxed">
            Lease offers are subject to credit approval, location, tax, title, registration, dealer fees, and availability. Final payment may vary.{deal.disclaimer ? ` ${deal.disclaimer}` : ""}
          </p>
        </div>

        <aside className="lg:col-span-5">
          <div className="sticky top-24 glass rounded-2xl p-7" data-testid="get-this-deal-form">
            <p className="label-mono mb-2">Reserve / Inquire</p>
            <h3 className="font-display text-2xl mb-5">Get this deal</h3>
            <form onSubmit={submit} className="space-y-3">
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="bg-[#0A0A0E] border-[#1A1A24] h-11 rounded-xl text-white" data-testid="deal-form-name" />
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-[#0A0A0E] border-[#1A1A24] h-11 rounded-xl text-white" data-testid="deal-form-email" />
              <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="bg-[#0A0A0E] border-[#1A1A24] h-11 rounded-xl text-white" data-testid="deal-form-phone" />
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything else? (color, timing, trade-in)" rows={3} className="w-full bg-[#0A0A0E] border border-[#1A1A24] rounded-xl text-white p-3 text-sm" data-testid="deal-form-notes" />
              <button type="submit" disabled={submitting} className="w-full silver-gradient text-[#050507] font-bold py-3.5 rounded-full disabled:opacity-50" data-testid="deal-form-submit">
                {submitting ? "Sending…" : "Reserve This Deal"}
              </button>
            </form>
          </div>
        </aside>
      </div>

      {similar.length > 0 && (
        <div className="mt-16 md:mt-20 border-t border-[#1A1A24] pt-10" data-testid="deal-similar-rail">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <p className="label-mono mb-1">You might also like</p>
              <h2 className="font-display text-2xl md:text-3xl font-light tracking-tighter">Similar deals</h2>
            </div>
            <Link to="/lease-deals" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5">
              View all <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {similar.map((s) => <SimilarDealCard key={s.id} s={s} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function SimilarDealCard({ s }) {
  const intel = INTEL[s.intelligence_score];
  return (
    <Link
      to={`/lease-deals/${s.id}`}
      className="group bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden hover:border-white/25 transition-colors block"
      data-testid={`deal-similar-card-${s.id}`}
    >
      <div className="relative aspect-[16/11] bg-[#050507] overflow-hidden">
        {s.image_url ? (
          <img src={resolveImageUrl(s.image_url)} alt={`${s.brand} ${s.model}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" onError={(e) => { e.currentTarget.src = "/placeholder-vehicle.svg"; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600 text-[10px] tracking-widest">NO IMAGE</div>
        )}
        {intel && (
          <span className={`absolute top-2.5 left-2.5 px-2 py-0.5 rounded-full border ${intel.bg} ${intel.cls} text-[9px] font-black tracking-widest flex items-center gap-1`}>
            <Sparkles className="w-2.5 h-2.5" /> {intel.label.toUpperCase()}
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="text-xs text-gray-500">{s.brand} · {s.year}</div>
        <div className="text-sm font-medium truncate">{s.model}</div>
        <div className="text-xs text-gray-500 truncate mb-2">{s.trim}</div>
        <div className="flex items-baseline gap-1">
          <span className="font-display text-lg">${Math.round(s.monthly_payment).toLocaleString()}</span>
          <span className="text-[11px] text-gray-500">/mo</span>
        </div>
      </div>
    </Link>
  );
}

function DealerRatingBlock({ dealerName, ratings, setRatings, dealId, user }) {
  const [form, setForm] = useState({ communication: 5, transparency: 5, delivery: 5, deal_accuracy: 5, comment: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!user) { toast.error("Sign in to rate this dealer"); window.location.href = "/login"; return; }
    setBusy(true);
    try {
      await api.post(`/dealers/by-name/${encodeURIComponent(dealerName)}/ratings`, { ...form, deal_id: dealId });
      const { data } = await api.get(`/dealers/by-name/${encodeURIComponent(dealerName)}/ratings`);
      setRatings(data);
      toast.success("Thanks — your rating is live.");
      setForm({ communication: 5, transparency: 5, delivery: 5, deal_accuracy: 5, comment: "" });
    } catch (e) { toast.error("Couldn't submit rating"); }
    finally { setBusy(false); }
  };

  const Bar = ({ label, value, icon: Icon }) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-400 flex items-center gap-1.5"><Icon className="w-3 h-3 text-silver" />{label}</span>
        <span className="text-xs text-white">{value ? value.toFixed(1) : "—"}</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full silver-gradient" style={{ width: `${((value || 0) / 5) * 100}%` }} />
      </div>
    </div>
  );

  return (
    <div className="mt-10 bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-7" data-testid="dealer-rating-block">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="label-mono mb-2">Dealer Transparency</div>
          <div className="font-display text-2xl">{dealerName}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-gray-500 tracking-wider uppercase">Overall</div>
          <div className="font-display text-4xl font-light flex items-center gap-1">
            {ratings?.overall ? ratings.overall.toFixed(1) : "—"}
            <Star className="w-4 h-4 text-yellow-300" />
          </div>
          <div className="text-[10px] text-gray-500">{ratings?.count || 0} {ratings?.count === 1 ? "rating" : "ratings"}</div>
        </div>
      </div>

      {ratings?.by_axis && (
        <div className="grid sm:grid-cols-2 gap-4 mb-7">
          <Bar label="Communication" value={ratings.by_axis.communication} icon={MessageSquare} />
          <Bar label="Transparency" value={ratings.by_axis.transparency} icon={Shield} />
          <Bar label="Delivery Experience" value={ratings.by_axis.delivery} icon={Truck} />
          <Bar label="Deal Accuracy" value={ratings.by_axis.deal_accuracy} icon={Handshake} />
        </div>
      )}

      {ratings?.recent?.length > 0 && (
        <div className="mb-7 space-y-3" data-testid="dealer-rating-recent">
          {ratings.recent.slice(0, 3).map((r) => (
            <div key={r.id} className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-white">{r.user_name}</span>
                <span className="text-[10px] text-gray-500">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              <div className="text-[11px] text-gray-400 mb-2">
                Comm {r.communication}/5 · Trans {r.transparency}/5 · Deliv {r.delivery}/5 · Acc {r.deal_accuracy}/5
              </div>
              {r.comment && <p className="text-sm text-gray-300">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Rate form */}
      <div className="border-t border-white/5 pt-6">
        <div className="label-mono mb-3">Rate this dealer</div>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          {[
            ["communication", "Communication", MessageSquare],
            ["transparency", "Transparency", Shield],
            ["delivery", "Delivery Experience", Truck],
            ["deal_accuracy", "Deal Accuracy", Handshake],
          ].map(([k, label, Icon]) => (
            <div key={k}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-gray-400 flex items-center gap-1.5"><Icon className="w-3 h-3 text-silver" />{label}</span>
                <span className="text-xs text-white">{form[k]}/5</span>
              </div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setForm({ ...form, [k]: n })} className={`flex-1 h-8 rounded ${form[k] >= n ? "silver-gradient" : "bg-white/5"}`} data-testid={`rate-${k}-${n}`}>
                    <Star className={`w-3 h-3 mx-auto ${form[k] >= n ? "text-[#050507] fill-current" : "text-gray-600"}`} />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <textarea
          rows={2}
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          placeholder="One sentence on what stood out (optional)"
          className="w-full bg-[#050507] border border-[#1A1A24] rounded-xl p-3 text-sm text-white mb-3"
          data-testid="dealer-rate-comment"
        />
        <button onClick={submit} disabled={busy} className="w-full silver-gradient text-[#050507] font-bold py-3 rounded-full disabled:opacity-50" data-testid="dealer-rate-submit">
          {busy ? "Submitting…" : "Submit Rating"}
        </button>
      </div>
    </div>
  );
}
